import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/auth/useAuth';
import { InkHold } from './InkHold';
import {
  isPinGateEnabled,
  isPinUnlocked,
  lockPin,
  pinUnlockRemainingMs,
} from './pinGate';
import { PinGateLanding } from './PinGateLanding';
import { resolvePinSurface } from './pinAccessPolicy';

/**
 * Site-wide soft launch PIN (same behaviour as HOTTR.men / HOTTR.tv).
 * Unlock lasts 24 hours. A signed-in person never sees the keypad.
 *
 * `/auth/callback` and `/auth/hop` are exempt so a world hop can start SSO
 * on this origin and redeem the one-time code without the wall eating it.
 */
export function RequirePin({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  const authenticated: boolean | undefined = loading ? undefined : user !== null;
  const [ok, setOk] = useState(() => isPinUnlocked());
  const onUnlocked = useCallback(() => setOk(true), []);

  useEffect(() => {
    if (!isPinGateEnabled()) {
      setOk(true);
      return;
    }
    if (!ok || authenticated) return;

    const recheck = () => {
      if (!isPinUnlocked()) {
        lockPin();
        setOk(false);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') recheck();
    };

    const remaining = pinUnlockRemainingMs();
    const expiryTimer =
      remaining > 0
        ? window.setTimeout(() => {
            lockPin();
            setOk(false);
          }, remaining + 250)
        : null;

    const interval = window.setInterval(recheck, 60_000);

    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (expiryTimer != null) window.clearTimeout(expiryTimer);
      window.clearInterval(interval);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ok, authenticated]);

  const surface = resolvePinSurface({
    pathname,
    authenticated,
    pinEnabled: isPinGateEnabled(),
    pinUnlocked: ok,
  });

  if (surface === 'hold') return <InkHold />;
  if (surface === 'keypad') {
    return <PinGateLanding onUnlocked={onUnlocked} />;
  }

  return <>{children}</>;
}
